import { useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { Spinner } from "@nextui-org/react";
import { X } from "lucide-react";
import {
  Drawer,
  DrawerBody,
  DrawerContent,
} from "@/shared/components/drawer";
import { useGetTeamsListQuery } from "@/modules/auth/requests";
import { ROUTES } from "@/shared/constants/routes";
import VendorLogo from "@/shared/components/VendorSearchAutocomplete/VendorLogo";
import { useAssessmentHistory } from "../../hooks/useAssessmentHistory";
import { VersionCard } from "./VersionCard";
import useVersionHistoryDrawerStore from "./useVersionHistoryDrawerStore";
import type { Assessment } from "../../types";

export function VersionHistoryDrawer() {
  const navigate = useNavigate();

  const { isOpen, vendorName, productName, vendorLogoUrl, closeDrawer } =
    useVersionHistoryDrawerStore();

  // Fetch team members for name resolution
  const { data: teamsData } = useGetTeamsListQuery({ isEnabled: isOpen });

  const { data, isLoading, error } = useAssessmentHistory(
    isOpen && vendorName ? { vendor_name: vendorName, product_name: productName || undefined } : null
  );

  // Navigate to session
  const handleNavigate = (assessment: Assessment) => {
    const isViewOnly = !assessment.is_active; // Archived = view only

    if (isViewOnly) {
      navigate(`/${ROUTES.AGENT}/${assessment.id}?mode=view`);
    } else {
      navigate(`/${ROUTES.AGENT}/${assessment.id}`);
    }

    closeDrawer();
  };

  // Sort by version descending (newest first)
  const sortedAssessments = useMemo(() => {
    if (!data?.history) return [];
    return [...data.history].sort((a, b) => b.version - a.version);
  }, [data?.history]);

  // Build title with product name if available
  const title = productName
    ? `${vendorName} ${productName} Assessment History`
    : `${vendorName} Assessment History`;

  // First letter for fallback avatar
  const firstLetter = vendorName?.trim().charAt(0).toUpperCase() || '?';

  if (!isOpen) {
    return null;
  }

  return (
    <Drawer isOpen={isOpen} placement={"right" as any} onClose={closeDrawer}>
      <DrawerContent className="drawer-content w-[488px]">
        {/* Header */}
        <div className="relative flex flex-col justify-center items-start px-6 py-4 h-[60px] bg-white border-b border-[#E2E8F0]">
          <div className="flex flex-row items-center gap-2 w-full h-7 pr-8">
            {vendorLogoUrl ? (
              <VendorLogo
                src={vendorLogoUrl}
                alt={vendorName || "Vendor"}
                needsAuthToken={true}
                size={28}
                radius="full"
                className="border border-[#E2E8F0]"
              />
            ) : (
              <div
                className="flex items-center justify-center flex-shrink-0"
                style={{
                  width: '28px',
                  height: '28px',
                  background: '#FAFAFA',
                  border: '1px solid #E2E8F0',
                  borderRadius: '24px',
                }}
              >
                <span className="font-['Inter'] font-semibold text-sm leading-5 text-[#71717A]">
                  {firstLetter}
                </span>
              </div>
            )}
            <h3 className="font-['Inter'] font-semibold text-lg leading-7 text-[#171717] truncate flex-1">
              {title}
            </h3>
          </div>
          {/* Close button */}
          <button
            type="button"
            onClick={closeDrawer}
            className="absolute right-6 top-1/2 -translate-y-1/2 p-0 hover:opacity-70 transition-opacity"
            aria-label="Close drawer"
          >
            <X className="w-4 h-4 text-[#62748E]" />
          </button>
        </div>

        <DrawerBody className="py-4 px-6 gap-0 flex flex-col overflow-y-auto">
          {isLoading && (
            <div className="flex items-center justify-center py-8">
              <Spinner size="lg" />
            </div>
          )}

          {error && (
            <div className="text-center py-8 text-red-500">
              Failed to load version history. Please try again.
            </div>
          )}

          {!isLoading && !error && sortedAssessments.length === 0 && (
            <div className="text-center py-8 text-[#62748E]">
              No version history available.
            </div>
          )}

          {!isLoading && !error && sortedAssessments.length > 0 && (
            <div className="space-y-3">
              {sortedAssessments.map((assessment) => (
                <VersionCard
                  key={assessment.id}
                  assessment={assessment}
                  onNavigate={handleNavigate}
                  memberIdNameMapping={teamsData?.memberIdNameMapping}
                />
              ))}
            </div>
          )}
        </DrawerBody>
      </DrawerContent>
    </Drawer>
  );
}

export { default as useVersionHistoryDrawerStore } from './useVersionHistoryDrawerStore';
